import React, { useState, useMemo, useEffect } from "react";
import {
  Modal, Form, InputNumber, Select, DatePicker, Input, Upload,
  message, Alert,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";
import { useQv4Theme } from "./helpers";

dayjs.extend(utc);

// Same cost-tier lookup PriceUpdateDrawer/PaymentLinkModal use to suggest a
// delivery charge from the order's total supplier cost.
let _cachedCostPrices = null;
let _costPricesFetch = null;
function fetchCostPricesOnce() {
  if (_cachedCostPrices?.length) return Promise.resolve(_cachedCostPrices);
  if (_costPricesFetch) return _costPricesFetch;
  _costPricesFetch = axios
    .post(`${API_DOMAIN}/whatsapp/get_costprices`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
    .then(res => {
      const rows = res.data?.data ?? res.data ?? [];
      const data = Array.isArray(rows) ? rows : [];
      if (data.length) _cachedCostPrices = data;
      _costPricesFetch = null;
      return data;
    })
    .catch(() => { _costPricesFetch = null; return []; });
  return _costPricesFetch;
}
function getTier(costPrices, price) {
  return costPrices.find(t => price >= t.MinPrice && price <= t.MaxPrice) ?? null;
}

const METHOD_OPTIONS = [
  { value: "cash",          label: "Cash" },
  { value: "card",          label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "pay_link",      label: "Pay By Link" },
  { value: "cheque",        label: "Cheque" },
  { value: "credit",        label: "Credit" },
  { value: "other",         label: "Other" },
];

const DEFAULT_VAT_PCT = 5;

const STAGE_OPTIONS = [
  { value: "advance",       label: "Advance" },
  { value: "balance",       label: "Balance" },
  { value: "full_payment",  label: "Full Payment" },
  { value: "refund",        label: "Refund" },
  { value: "adjustment",    label: "Adjustment" },
];

const SOURCE_OPTIONS = [
  { value: "office",    label: "Office" },
  { value: "bank",      label: "Bank" },
  { value: "online",    label: "Online" },
  { value: "delivery",  label: "Delivery" },
  { value: "whatsapp",  label: "WhatsApp" },
];

const ONLINE_METHODS = new Set(["bank_transfer", "pay_link"]);

// cash/card = immediately received; others start as pending
function deriveStatus(method) {
  if (!method) return null;
  return ["cash", "card"].includes(method) ? "received" : "pending";
}

// full_payment when nothing's outstanding, advance for a first partial
// payment, balance for any payment after something's already been collected.
function stageFor(balance, saleAmt, paidAmt) {
  if (balance === saleAmt) return "full_payment";
  if (paidAmt === 0) return "advance";
  return "balance";
}

export function RecordPaymentModal({ open, queryPk, userId, afterSales, parts, onClose, onSuccess }) {
  const { t, isDark } = useQv4Theme();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [parkTask, setParkTask] = useState(false);
  const [method, setMethod] = useState(null);
  const headers = { accessToken: localStorage.getItem("accessToken") };

  const isOnline = ONLINE_METHODS.has(method);
  const derivedStatus = deriveStatus(method);

  const hasAfterSales = !!afterSales;

  // No query_after_sales row exists yet — there's no stored invoice total to
  // fall back on. Compute one from the selected quotes' selling price, plus
  // staff-adjustable delivery/VAT, the same inputs PaymentLinkModal's own
  // preview uses (minus its processing fee, which only applies to gateway
  // payments — not relevant for a manual cash/bank/cheque entry).
  const [manualDelivery, setManualDelivery] = useState(0);
  const [manualVatPct, setManualVatPct] = useState(DEFAULT_VAT_PCT);

  const partsTotal = useMemo(() => {
    if (hasAfterSales) return 0;
    return (parts ?? [])
      .filter(p => p.selected_quote_id && p.selling_price != null)
      .reduce((s, p) => s + Number(p.selling_price), 0);
  }, [parts, hasAfterSales]);

  const computedInvoiceTotal = useMemo(() => {
    const vatAmount = Math.round((partsTotal + Number(manualDelivery || 0)) * (manualVatPct / 100) * 100) / 100;
    return Math.round((partsTotal + Number(manualDelivery || 0) + vatAmount) * 100) / 100;
  }, [partsTotal, manualDelivery, manualVatPct]);

  // Delivery auto-fill: same tier lookup as PriceUpdateDrawer/PaymentLinkModal,
  // keyed off the total supplier cost (not the customer-facing selling price).
  const [costPrices, setCostPrices] = useState([]);
  useEffect(() => {
    if (!open || hasAfterSales) return;
    fetchCostPricesOnce().then(setCostPrices);
  }, [open, hasAfterSales]);

  const costTotal = useMemo(() => {
    if (hasAfterSales) return 0;
    return (parts ?? []).reduce((s, p) => {
      if (!p.selected_quote_id) return s;
      const q = (p.supplier_quotes ?? p.quotes ?? []).find(q => q.id === p.selected_quote_id);
      return s + Number(q?.quoted_price || 0);
    }, 0);
  }, [parts, hasAfterSales]);

  // Sets the suggested delivery once tiers + the cost total are both ready —
  // re-runs only if those actually change, so a manual edit afterward sticks.
  useEffect(() => {
    if (!open || hasAfterSales || !costTotal || !costPrices.length) return;
    const tier = getTier(costPrices, costTotal);
    const d = tier ? Number(tier.DeliveryCost) : 0;
    setManualDelivery(d);
    const vatAmount = Math.round((partsTotal + d) * (manualVatPct / 100) * 100) / 100;
    resyncAmountDefaults(Math.round((partsTotal + d + vatAmount) * 100) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasAfterSales, costTotal, costPrices]);

  // Unify the two cases (an existing after-sales record vs. this being the
  // very first payment activity) into one set of figures the rest of the
  // form works off of.
  const effectiveSaleAmount = hasAfterSales ? Number(afterSales.sale_amount || 0) : computedInvoiceTotal;
  const effectivePaidAmount = hasAfterSales ? Number(afterSales.paid_amount || 0) : 0;
  const effectiveBalance    = Math.max(0, effectiveSaleAmount - effectivePaidAmount);

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setParkTask(false);
    setMethod(null);
    setManualDelivery(0);
    setManualVatPct(DEFAULT_VAT_PCT);
    onClose();
  };

  const handleAmountChange = (val) => {
    if (!effectiveSaleAmount || !val) return;
    const currentStage = form.getFieldValue("payment_stage");
    if (val < effectiveSaleAmount) {
      if (currentStage === "full_payment") {
        form.setFieldValue("payment_stage", "advance");
        setParkTask(true);
      }
    } else {
      if (currentStage === "advance") {
        form.setFieldValue("payment_stage", "full_payment");
        setParkTask(false);
      }
    }
  };

  // Re-sync the suggested Amount/Stage whenever the invoice calc inputs
  // change, since effectiveSaleAmount/Balance shift with them — only
  // relevant pre-afterSales, but harmless (a no-op) once it exists.
  const resyncAmountDefaults = (nextTotal) => {
    const nextBalance = Math.max(0, nextTotal - effectivePaidAmount);
    form.setFieldsValue({
      amount: nextBalance || undefined,
      payment_stage: stageFor(nextBalance, nextTotal, effectivePaidAmount),
    });
    setParkTask(stageFor(nextBalance, nextTotal, effectivePaidAmount) === "advance");
  };

  const handleDeliveryChange = (val) => {
    setManualDelivery(val ?? 0);
    const vatAmount = Math.round((partsTotal + Number(val || 0)) * (manualVatPct / 100) * 100) / 100;
    resyncAmountDefaults(Math.round((partsTotal + Number(val || 0) + vatAmount) * 100) / 100);
  };

  const handleVatChange = (val) => {
    const pct = val ?? 0;
    setManualVatPct(pct);
    const vatAmount = Math.round((partsTotal + Number(manualDelivery || 0)) * (pct / 100) * 100) / 100;
    resyncAmountDefaults(Math.round((partsTotal + Number(manualDelivery || 0) + vatAmount) * 100) / 100);
  };

  const handleStageChange = (val) => {
    if (val === "advance") {
      setParkTask(true);
    } else if (val === "full_payment" || val === "balance") {
      setParkTask(false);
    }
  };

  const handleMethodChange = (val) => {
    setMethod(val);
    if (ONLINE_METHODS.has(val)) {
      form.setFieldsValue({
        payment_source: "online",
        collected_from: undefined,
        collection_location: undefined,
      });
    }
    if (["cash", "card"].includes(val)) {
      form.setFieldValue("paid_at", dayjs());
    }
  };

  const handleOk = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const proofUrls = await Promise.all(
        fileList.map(async f => {
          const fd = new FormData();
          fd.append("file", f.originFileObj);
          const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
          return res.data;
        })
      );

      const status = derivedStatus || "pending";
      const res = await axios.post(
        `${API_DOMAIN}/queries/${queryPk}/payments`,
        {
          userId,
          sale_amount: effectiveSaleAmount || values.amount,
          amount: values.amount,
          payment_method: values.payment_method,
          payment_stage: values.payment_stage,
          payment_status: status,
          paid_at: values.paid_at
            ? values.paid_at.utc().format("YYYY-MM-DD HH:mm:ss")
            : null,
          payment_reference: values.payment_reference?.trim() || null,
          payment_source: values.payment_source || null,
          collected_from: isOnline ? null : (values.collected_from?.trim() || null),
          collection_location: isOnline ? null : (values.collection_location?.trim() || null),
          received_by: status === "received" ? userId : null,
          remarks: values.remarks?.trim() || null,
          proof_images: proofUrls,
          ...(parkTask && values.park_until ? {
            park_until: values.park_until.utc().format("YYYY-MM-DD HH:mm:ss"),
            park_reason: values.park_reason?.trim() || null,
          } : {}),
        },
        { headers }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      message.success("Payment recorded");
      onSuccess();
      handleClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to record payment");
    } finally { setSaving(false); }
  };

  const getDefaultValues = () => {
    return {
      amount: effectiveBalance || undefined,
      payment_stage: stageFor(effectiveBalance, effectiveSaleAmount, effectivePaidAmount),
      paid_at: dayjs(),
    };
  };

  return (
    <Modal
      open={open}
      title="Record Payment"
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Record"
      destroyOnHidden
      styles={{ body: { overflowY: "auto", maxHeight: "calc(90vh - 160px)", paddingRight: 4 } }}
      afterOpenChange={isOpen => {
        if (isOpen) form.setFieldsValue(getDefaultValues());
      }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>

        {!hasAfterSales && (
          <div style={{
            marginBottom: 16, padding: "10px 12px", borderRadius: 6,
            background: isDark ? "rgba(69,153,34,0.12)" : "#f6ffed", border: "1px solid #b7eb8f",
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#389e0d", marginBottom: 8 }}>
              No payment recorded yet — set the invoice total this first payment establishes
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Parts Total (AED)</div>
                <InputNumber value={partsTotal} disabled style={{ width: "100%" }} precision={2} prefix="AED" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Delivery (AED)</div>
                <InputNumber
                  value={manualDelivery} onChange={handleDeliveryChange}
                  min={0} precision={2} style={{ width: "100%" }} prefix="AED"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>VAT %</div>
                <InputNumber
                  value={manualVatPct} onChange={handleVatChange}
                  min={0} max={100} precision={2} style={{ width: "100%" }} addonAfter="%"
                />
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#389e0d" }}>
              Invoice Total: AED {computedInvoiceTotal.toFixed(2)}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <Form.Item name="amount" label="Amount (AED)" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
            <InputNumber
              min={0} precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
              prefix="AED"
              onChange={handleAmountChange}
            />
          </Form.Item>
          <Form.Item name="payment_stage" label="Stage" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
            <Select placeholder="Select..." options={STAGE_OPTIONS} onChange={handleStageChange} />
          </Form.Item>
        </div>

        <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true, message: "Required" }]}>
          <Select
            placeholder="Select method..."
            options={METHOD_OPTIONS}
            onChange={handleMethodChange}
          />
        </Form.Item>

        {derivedStatus && (
          <Alert
            type={derivedStatus === "received" ? "success" : "info"}
            message={
              derivedStatus === "received"
                ? "This payment will be recorded as Received immediately."
                : "This payment will be recorded as Pending — confirm later via Mark as Received."
            }
            showIcon
            style={{ marginBottom: 16, padding: "4px 12px", fontSize: 12 }}
          />
        )}

        <Form.Item
          name="paid_at"
          label={method === "credit" ? "Payment Due Date" : derivedStatus === "pending" ? "Expected / Transaction Date" : "Payment Date & Time"}
          rules={derivedStatus === "received" ? [{ required: true, message: "Required" }] : []}
        >
          <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
        </Form.Item>

        <Form.Item name="payment_reference" label="Payment Reference">
          <Input placeholder="e.g. TRN-20260628-001, cheque number, transfer screenshot ref" />
        </Form.Item>

        <Form.Item name="payment_source" label="Source">
          <Select
            placeholder="Where?"
            options={SOURCE_OPTIONS}
            allowClear
            disabled={isOnline}
          />
        </Form.Item>

        {!isOnline && (
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="collected_from" label="Collected From" style={{ flex: 1 }}>
              <Input placeholder="e.g. Customer, Insurance, Corporate" />
            </Form.Item>
            <Form.Item name="collection_location" label="Collection Location" style={{ flex: 1 }}>
              <Input placeholder="e.g. Office, Workshop" />
            </Form.Item>
          </div>
        )}

        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="Any notes about this payment" />
        </Form.Item>

        <Form.Item label="Proof Images (optional)">
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => setFileList(fl)}
            accept="image/*"
            multiple
          >
            {fileList.length < 5 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 4, fontSize: 11 }}>Add</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item label="Park task for follow-up?">
          <input
            type="checkbox"
            checked={parkTask}
            onChange={e => setParkTask(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <span style={{ fontSize: 12, color: t.textSub }}>Partial payment — customer will return</span>
        </Form.Item>
        {parkTask && (
          <>
            <Form.Item name="park_until" label="Follow-up Date & Time" rules={[{ required: true, message: "Required" }]}>
              <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="park_reason" label="Park Reason">
              <Input.TextArea rows={2} placeholder="e.g. Customer will pay balance tomorrow" />
            </Form.Item>
          </>
        )}

      </Form>
    </Modal>
  );
}
